import { ref, computed, getCurrentInstance } from 'vue'
import store from '@/store'

export default function useCampaign(campaign_id = null) {

    // ==================== LISTADO ====================
    const loading = ref(false)
    const loadingEstadisticas = ref(false)
    const loadingCompanias = ref(false)

    const filters = ref({
        search: '',
        activo: '',
        tipo_campana: '',
        teatro_company_ids: [],
        page: 1,
        per_page: 10
    })

    const campaigns = ref({
        data: [],
        meta: {}
    })

    const columns = [
        { key: 'estado', label: 'Estado', field: 'activa' },
        { key: 'nombre', label: 'Nombre', field: 'nombre' },
        { key: 'tipo', label: 'Disparador', field: 'tipo_campana' },
        { key: 'promotor', label: 'Promotor', field: 'compania.nombre' },
        { key: 'canales', label: 'Canales' },
        { key: 'retraso', label: 'Retraso' },
        { key: 'acciones', label: 'Acciones' }
    ]

    const companies = ref([])

    const detalles_economicos = ref({
        mensajes_enviados: 0,
        coste_total: 0
    })

    const teatro = computed(() => store.getters.getTeatro)

    function getCampaigns() {
        loading.value = true

        const params = {
            filters: filters.value
        }

        store.dispatch('BACK_UPSELLING_INDEX', params).then(res => {
            campaigns.value = res.data
        }).catch(error => {
            console.error(error)
        }).finally(() => {
            loading.value = false
        })
    }

    function getEstadisticas() {
        loadingEstadisticas.value = true

        const params = {
            filters: filters.value
        }

        store.dispatch('BACK_UPSELLING_DETALLES_ECONOMICOS', params).then(res => {
            detalles_economicos.value = res.data
        }).catch(error => {
            console.error(error)
        }).finally(() => {
            loadingEstadisticas.value = false
        })
    }

    function getCompanies() {
        loadingCompanias.value = true

        const params = {
            filters: {},
            teatro_id: teatro.value.id,
            to_select: true
        }

        store.dispatch('BACK_TEATRO_COMPANIA_EVENTOS_INDEX', params).then(res => {
            companies.value = res.data.data
        }).catch(error => {
            console.error(error)
        }).finally(() => {
            loadingCompanias.value = false
        })
    }

    const disabled = computed(() => loading.value || loadingEstadisticas.value || loadingCompanias.value)

    // ==================== DETALLE ====================
    const loadingDetalle = ref(false);

    const campaignDetails = ref({
        id: null,
        nombre: null,
        tipo_campana: null,
        tipos_abonos: [],
        eventos: [],
        equipos: [],
        envio_inmediato: false,
        retraso_envio: null,
        whatsapp_template: { id: null, nombre: null },
        activa: false,
        canales: [],
        asunto_email: '',
        contenido_html: '',
        estadisticas: {
            total_enviados: 0,
            tasa_apertura: 0,
            coste_acumulado: 0,
            envios_por_dia: [],
            ultimos_envios: []
        }
    });

    const ultimos7 = computed(() =>
        [...(campaignDetails.value.estadisticas.envios_por_dia ?? [])]
            .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
            .slice(-7)
    );

    const getCampaignDetails = async() => {
        try {
            loadingDetalle.value = true;
            const response = await store.dispatch('BACK_UPSELLING_SHOW', { campana_id: campaign_id });
            const data = response.data.data;
            campaignDetails.value = {
                ...data,
                tipos_abonos: data.tipos_abonos ?? [],
                eventos: data.eventos ?? [],
                equipos: (data.equipos ?? []).map(normalizeEquipo),
                canales: [
                    ...(data.tipo_whatsapps ? ['whatsapp'] : []),
                    ...(data.tipo_emails ? ['email'] : [])
                ],
                asunto_email: data.asunto_email ?? '',
                contenido_html: data.contenido_html ?? '',
                estadisticas: {
                    ...data.estadisticas,
                    envios_por_dia: data.estadisticas?.envios_por_dia ?? [],
                    ultimos_envios: (data.estadisticas?.ultimos_envios ?? []).map(normalizeUltimoEnvio)
                }
            };
        } catch (error) {
            console.error(error?.response?.data?.message || 'Error al cargar datos de campaña');
        } finally {
            loadingDetalle.value = false;
        }
    };

    async function deleteCampaign(campana_id) {
        try {
            const response = await store.dispatch('BACK_UPSELLING_ELIMINAR', { campana_id })
            getCampaigns()
            return response
        } catch (error) {
            console.error(error)
            throw error
        }
    }

    // ==================== TOGGLE ====================
    const instance = getCurrentInstance()
    const $toast = instance?.proxy?.$toast
    const loadingToggleIds = ref({})
    const optimisticActivas = ref({})

    function isToggling(id) {
        return !!loadingToggleIds.value[id]
    }

    async function toggleCampaign(row, val) {
        if (isToggling(row.id)) return

        loadingToggleIds.value = { ...loadingToggleIds.value, [row.id]: true }
        optimisticActivas.value = { ...optimisticActivas.value, [row.id]: val }

        try {
            const response = await store.dispatch('BACK_UPSELLING_EDITAR', { campana_id: row.id, activa: val })
            row.activa = val
            $toast?.success(response.data.message || 'Campaña actualizada')
        } catch (err) {
            console.error(err)
            optimisticActivas.value = { ...optimisticActivas.value, [row.id]: !val }
            $toast?.error(err?.response?.data?.message || 'Error al actualizar campaña')
        } finally {
            const cleanOpt = { ...optimisticActivas.value }
            delete cleanOpt[row.id]
            optimisticActivas.value = cleanOpt

            const cleanLoading = { ...loadingToggleIds.value }
            delete cleanLoading[row.id]
            loadingToggleIds.value = cleanLoading

        }
    }
    
    // Deriva el flag "enviar a todos" comparando la selección guardada contra el
    // catálogo disponible. El backend no expone un flag explícito, por lo que
    // consideramos "todos" cuando la selección cubre el catálogo completo.
    // Normaliza el valor de segmentación que viene del backend al
    // término que usa la UI. El API envía 'bases', pero internamente
    // el formulario y los watchers usan 'deportistas'.
    const SEGMENTACION_MAP = { bases: 'deportistas' }

    function normalizeEquipo(equipo) {
        if (!equipo) return equipo
        return {
            id: equipo.id,
            nombre: equipo.nombre ?? equipo.name ?? ''
        }
    }

    function normalizeUltimoEnvio(envio) {
        if (!envio?.user) return envio

        return {
            ...envio,
            user: {
                ...envio.user,
                nombre: envio.user.nombre ?? envio.user.name ?? ''
            }
        }
    }

    function normalizeSegmentacion(value) {
        const lowered = (value || '').toString().toLowerCase()
        return SEGMENTACION_MAP[lowered] ?? lowered
    }

    function deriveEnviarATodos(selected, catalog) {
        if (!Array.isArray(selected) || !Array.isArray(catalog)) return false
        if (catalog.length === 0) return false
        const selectedIds = new Set(selected.map(item => item.id ?? item))
        const catalogIds = new Set(catalog.map(item => item.id ?? item))
        if (selectedIds.size !== catalogIds.size) return false
        for (const id of selectedIds) {
            if (!catalogIds.has(id)) return false
        }
        return true
    }

    function buildInitialForm(detail = null, catalogs = {}) {
        if (!detail) {
            return {
                nombre: '',
                promotor: null,
                segmentacion: '',
                abonados: [],
                enviarATodosAbonos: false,
                enviarATodosEquipos: false,
                enviarATodosEventos: false,
                retraso: 'inmediato',
                retrasoPersonalizado: 30,
                retrasoUnidad: 'minutos',
                restriccionProductos: false,
                restriccionParking: false,
                restriccionSeguros: false,
                activa: false,
                plantilla: null,
                eventos: [],
                equipos: [],
                canales: [],
                email_subject: '',
                email_content: ''
            }
        }

        let retraso = 'inmediato'
        let retrasoPersonalizado = 30
        let retrasoUnidad = 'minutos'
        
        if (!detail.envio_inmediato && detail.retraso_envio != null) {
            if ([120, 1440, 2880].includes(detail.retraso_envio)) {
                retraso = String(detail.retraso_envio)
            } else if (detail.retraso_envio % 60 === 0) {
                retraso = 'personalizar'
                retrasoPersonalizado = detail.retraso_envio / 60
                retrasoUnidad = 'horas'
            } else {
                retraso = 'personalizar'
                retrasoPersonalizado = detail.retraso_envio
                retrasoUnidad = 'minutos'
            }
        }

        // Rehydrate canales from tipo_whatsapps/tipo_emails booleans
        const canales = []
        if (detail.tipo_whatsapps) {
            canales.push('whatsapp')
        }
        if (detail.tipo_emails) {
            canales.push('email')
        }
        // Fallback: if canales array exists in detail (legacy), use it
        if (Array.isArray(detail.canales) && detail.canales.length > 0) {
            canales.length = 0
            canales.push(...detail.canales)
        }

        const selectedAbonos = detail.tipos_abonos || []
        const selectedEventos = detail.eventos || []
        const selectedEquipos = detail.equipos || []

        return {
            id: detail.id,
            nombre: detail.nombre || '',
            promotor: detail.compania
                ? { id: detail.compania.id, label: detail.compania.nombre }
                : (detail.teatro_company_id ? { id: detail.teatro_company_id } : null),
            segmentacion: normalizeSegmentacion(detail.tipo_campana),
            abonados: selectedAbonos,
            eventos: selectedEventos,
            equipos: selectedEquipos,
            // Los catálogos no suelen estar disponibles cuando se construye
            // el form inicial (viven en useFormUpselling y se cargan async).
            // Cuando se pasan por `catalogs`, derivamos el flag comparando
            // la selección contra el catálogo completo. En caso contrario
            // los flags quedan en false y se re-derivan al cargar catálogos.
            enviarATodosAbonos: deriveEnviarATodos(selectedAbonos, catalogs.abonados),
            enviarATodosEquipos: deriveEnviarATodos(selectedEquipos, catalogs.equipos),
            enviarATodosEventos: deriveEnviarATodos(selectedEventos, catalogs.eventos),
            retraso,
            retrasoPersonalizado,
            retrasoUnidad,
            restriccionProductos: !!detail.producto_comprado,
            restriccionParking: !!detail.plaza_parking_comprada,
            restriccionSeguros: !!detail.seguro_comprado,
            activa: !!detail.activa,
            plantilla: detail.whatsapp_template_id
                ? { id: Number(detail.whatsapp_template_id), label: detail.whatsapp_template?.nombre || `Template ${detail.whatsapp_template_id}` }
                : null,
            canales,
            email_subject: detail.asunto_email || '',
            email_content: detail.contenido_html || ''
        }
    }

    return {
        loading,
        loadingEstadisticas,
        loadingCompanias,
        filters,
        campaigns,
        columns,
        companies,
        detalles_economicos,
        disabled,
        getEstadisticas,
        getCampaigns,
        getCompanies,
        campaignDetails,
        loadingDetalle,
        ultimos7,
        getCampaignDetails,
        deleteCampaign,
        loadingToggleIds,
        optimisticActivas,
        isToggling,
        toggleCampaign,
        buildInitialForm,
        deriveEnviarATodos,
        normalizeSegmentacion
    }
}
